import {DateTime} from 'luxon';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../libs/shared/helpers/src/internationalization/react';
import {Handler, HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {IconButton} from '../../../libs/shared/ui/src/components/buttons/iconButton';
import {Dropdown} from '../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../libs/shared/ui/src/theme/userThemeConsumer';
import {
  CalendarTypesEnum,
  ChangeCalendarFocusedDayMethodsEnum,
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../services/tracking';
import {CalendarViewShiftDirectionsEnum} from '../../store/panels/calendars/calendarsPanelState';
import {DatePickerContainer} from '../common/dateTime/datePickerContainer';

/*
 * Props.
 */

interface SideCalendarsControlsProps {
  focusedDate: number;
  onFocusedDateSelect: HandlerOf<number>;
  onCalendarShiftClick: HandlerOf<CalendarViewShiftDirectionsEnum>;
  onTodayButtonClick: Handler;
}

/*
 * Style.
 */

const StyledControlsDiv = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0px 7px 8px 16px;
  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
`;

const StyledNavigationSectionDiv = styled.div`
  display: grid;
  grid-template-columns: auto auto auto;
  grid-column-gap: 0;
  align-items: center;
`;

const StyledDateSectionWrapperDiv = styled.div`
  margin-left: -6px;
`;

interface DatePickerStyleProps {
  $isOpen: boolean;
}

const StyledDatePickerDiv = styled.div<DatePickerStyleProps>`
  display: flex;
  align-items: center;
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.veryLarge};
  font-weight: ${fontWeights.bold};
  padding: 4px 4px 4px 7px;
  border-radius: 4px;

  background-color: ${(p) => (p.$isOpen ? p.theme.alphas.gray10 : p.theme.foreground)};

  &:hover {
    background: ${(p) => p.theme.alphas.gray10};
  }
`;

const StyledDatePickerIconDiv = styled.div<DatePickerStyleProps>`
  margin-left: 3px;
  transform: ${(p) => (p.$isOpen ? 'rotateZ(180deg)' : 'none')};
`;

const StyledDatePickerWrapperDiv = styled.div`
  padding: 12px 16px;
`;

/*
 * Component.
 */

export const SideCalendarsControls: FC<SideCalendarsControlsProps> = (props) => (
  <StyledControlsDiv>
    <StyledDateSectionWrapperDiv>
      <PopoverCoordinator
        renderButton={(isOpen) => renderButton(isOpen, props.focusedDate)}
        renderModal={(popoverProps) => renderDatePicker(popoverProps, props)}
      />
    </StyledDateSectionWrapperDiv>
    {renderNavigationSection(props)}
  </StyledControlsDiv>
);

/*
 * DatePicker Button.
 */

function renderButton(isOpen: boolean, focusedDate: number) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledDatePickerDiv $isOpen={isOpen}>
          {DateTime.fromMillis(focusedDate).toFormat('EEE, MMM d')}
          <StyledDatePickerIconDiv $isOpen={isOpen}>
            <Icon name="chevronExpandTiny" color={theme.greys.shade70} />
          </StyledDatePickerIconDiv>
        </StyledDatePickerDiv>
      )}
    </UserThemeConsumer>
  );
}

/*
 * DatePicker.
 */

function renderDatePicker(popoverProps: PopoverTransferProps, props: SideCalendarsControlsProps) {
  const onSelectWithClose = (newDateTime: DateTime) => {
    props.onFocusedDateSelect(newDateTime.valueOf());
    popoverProps.onRequestClose();

    trackInterfaceEvent(InterfaceEventsEnum.CHANGE_CALENDAR_FOCUSED_DAY, {
      method: ChangeCalendarFocusedDayMethodsEnum.DAY_PICKER,
      calendar_type: CalendarTypesEnum.SIDE,
    });
  };

  return (
    <Dropdown {...popoverProps} width={250}>
      <StyledDatePickerWrapperDiv>
        <DatePickerContainer
          selectedDate={DateTime.fromMillis(props.focusedDate)}
          onDateSelect={onSelectWithClose}
        />
      </StyledDatePickerWrapperDiv>
    </Dropdown>
  );
}

/*
 * Navigation section.
 */

function renderNavigationSection(props: SideCalendarsControlsProps) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledNavigationSectionDiv>
          <IconButton
            iconName="chevronLeftSmall"
            label={<Trans id="calendar-controls-previous">Previous</Trans>}
            color={theme.greys.shade70}
            hoverColor={theme.greys.shade80}
            onClick={() => props.onCalendarShiftClick(CalendarViewShiftDirectionsEnum.PREVIOUS)}
          />
          <Button
            variant={ButtonVariantsEnum.SECONDARY}
            size={VisualSizesEnum.SMALL}
            onClick={props.onTodayButtonClick}
          >
            <Trans id="calendar-controls-today">Today</Trans>
          </Button>
          <IconButton
            iconName="chevronRightSmall"
            label={<Trans id="calendar-controls-next">Next</Trans>}
            color={theme.greys.shade70}
            hoverColor={theme.greys.shade80}
            onClick={() => props.onCalendarShiftClick(CalendarViewShiftDirectionsEnum.NEXT)}
          />
        </StyledNavigationSectionDiv>
      )}
    </UserThemeConsumer>
  );
}
